import { Button, TextButton } from '@creditornot/cb-button';
import { Pencil } from '@creditornot/cb-icons';
import { typographyCss, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { OrderApiCredentials } from 'apis/restaurant-api/merchant-admin/order-api-credentials/types';
import { Typography as T } from 'common/typography';

import OrderApiEventSubscription from './OrderApiEventSubscription';
import OrderApiWebhookModal from './OrderApiWebhookModal';
import { useOrderApiWebhook } from '../hooks';

type Props = {
  merchantId: string;
  orderApiMetadata: OrderApiCredentials;
};

type WebhookIntroductionProps = {
  onOpenModal: (open: boolean) => void;
};

const WebhookContentIntroductionContainer = styled.div`
  min-height: 75px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 64px 16px;
`;

const WebhookContentIntroContent = styled.div`
  ${typographyCss.Body2()}
  color: ${cbSemanticCssVars.textSecondary};
  font-weight: bold;
`;

const WebhookContentIntroSubContent = styled.div`
  ${typographyCss.Body3()}
  color: ${cbSemanticCssVars.textSecondary};
  margin-block-end: 30px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 26px 24px;
  color: ${cbSemanticCssVars.textPrimary};
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const WebhookIntroduction = ({ onOpenModal }: WebhookIntroductionProps) => {
  return (
    <WebhookContentIntroductionContainer>
      <WebhookContentIntroContent>
        <FormattedMessage id="merchant-integrations.order-api.webhook.intro" />
      </WebhookContentIntroContent>
      <WebhookContentIntroSubContent>
        <FormattedMessage id="merchant-integrations.order-api.webhook.intro-details" />
      </WebhookContentIntroSubContent>
      <Button size="small" variant="blue" onClick={() => onOpenModal(true)}>
        <FormattedMessage id="merchant-integrations.menu-api-intro-action" />
      </Button>
    </WebhookContentIntroductionContainer>
  );
};

const OrderApiWebhook = ({ merchantId, orderApiMetadata }: Props) => {
  const [editWebhookModalOpen, setEditWebhookModalOpen] = useState(false);
  const { webhook: existingWebhook, refresh } = useOrderApiWebhook(merchantId, orderApiMetadata.id);

  return (
    <>
      <Header>
        <T.Heading6>
          <FormattedMessage id="merchant-integrations.order-api.webhook.title" />
        </T.Heading6>
        <T.Heading6>
          {existingWebhook && (
            <>
              <TextButton icon={<Pencil />} onClick={() => setEditWebhookModalOpen(true)}>
                <FormattedMessage id="merchant-integrations.order-api.webhook.edit-header" />
              </TextButton>
            </>
          )}
        </T.Heading6>
      </Header>
      {existingWebhook ? (
        <OrderApiEventSubscription merchantId={merchantId} webhookMetadata={existingWebhook} />
      ) : (
        <WebhookIntroduction onOpenModal={setEditWebhookModalOpen} />
      )}
      {orderApiMetadata && (
        <OrderApiWebhookModal
          orderApiMetadata={orderApiMetadata}
          existingWebhook={existingWebhook}
          isOpen={editWebhookModalOpen}
          onClose={() => {
            refresh();
            setEditWebhookModalOpen(false);
          }}
          merchantId={merchantId}
        />
      )}
    </>
  );
};

export default OrderApiWebhook;
